import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import WanTrafficCard from './WanTrafficCard';
import {
   setDeviceWan,
   getDeviceWanPassword,
   type ApiVendoDevice,
   type WanConfigPayload,
} from '@/api/vendo';

/**
 * "Internet (WAN)" tab — how an OpenWRT router (Ruijie, OrangePi) gets its internet.
 *
 * Most boxes sit behind someone else's router and are handed an address, which
 * is what the image does out of the box. This tab is for the operator who wants
 * the SmartFi box wired straight to the ISP instead: fibre lines here almost
 * always need the router itself to log in with a username and password before
 * any internet arrives.
 *
 * The screen is built around one fact: a wrong ISP password does not fail
 * loudly, it takes the box off the internet — and the box's management tunnel
 * with it. Nobody could then reach it to correct the mistake. So the box tests
 * every change itself and puts the old settings back if the new ones cannot
 * reach the world, and this tab's job is to say that plainly BEFORE the
 * operator commits, then report which way it went.
 */

type Proto = 'dhcp' | 'static' | 'pppoe';

type WanState = NonNullable<NonNullable<ApiVendoDevice['router']>['wan']>;

interface Props {
   deviceId: string;
   wan?: WanState | null;
   /** The router's own internet verdict + when it last spoke, for the traffic
    *  card. Passed down rather than re-fetched: the parent already has them. */
   diag?: NonNullable<ApiVendoDevice['router']>['diag'];
   lastSeenAt?: string | null;
   wanTraffic?: NonNullable<ApiVendoDevice['router']>['wanTraffic'];
   /** Router liveness from the parent. A change cannot be pushed at a box that
    *  is already unreachable — say so on the button rather than failing after. */
   routerStatus?: 'connected' | 'agent-down' | 'disconnected' | 'unprovisioned';
   /** Re-pull the device so the verdict shows up without a page reload. */
   onRefresh?: () => void;
}

const MODES: { key: Proto; label: string; blurb: string }[] = [
   {
      key: 'dhcp',
      label: 'Automatic',
      blurb:
         'The router is behind another router or a modem, and gets its address automatically. This is how every router starts.',
   },
   {
      key: 'pppoe',
      label: 'PPPoE login',
      blurb:
         'The router is wired straight to the ISP\u2019s fibre or DSL modem, and logs in to the ISP itself. Common on PLDT, Converge and Globe.',
   },
   {
      key: 'static',
      label: 'Fixed address',
      blurb:
         'The ISP gave a permanent address to type in by hand. Rare — only use this if your ISP told you to.',
   },
];

export const isIpv4 = (v: string) =>
   /^\d{1,3}(\.\d{1,3}){3}$/.test(v) &&
   v.split('.').every((p) => Number(p) <= 255 && !(p.length > 1 && p[0] === '0'));

const toInt = (v: string) =>
   v.split('.').reduce((acc, part) => acc * 256 + Number(part), 0);

export const isNetmask = (v: string) => {
   if (!isIpv4(v)) return false;
   const n = toInt(v);
   if (n === 0) return false;
   const inverted = ~n >>> 0;
   return ((inverted + 1) & inverted) === 0;
};

export interface WanFormValues {
   proto: Proto;
   /** The internet VLAN tag as typed; empty = untagged. */
   vlan: string;
   username: string;
   password: string;
   /** Is a password already saved server-side? Then an empty box means "keep
    *  it", not "no password". */
   hasPassword: boolean;
   ipaddr: string;
   netmask: string;
   gateway: string;
   dns: string;
}

/**
 * Check the form and build what to send.
 *
 * Separate from the component because these are the rules that stop an
 * operator from cutting their own box off the internet, and they should be
 * provable without rendering anything. The server checks the same things
 * again — that is not duplication, it is the difference between a message an
 * operator can act on and a rejection from a router.
 */
export function validateWanForm(v: WanFormValues): {
   errors: Record<string, string>;
   payload: WanConfigPayload | null;
} {
   const errors: Record<string, string> = {};
   const payload: WanConfigPayload = { proto: v.proto };

   // Optional, independent of the mode: a managed switch or fiber modem can
   // hand the internet over on a VLAN tag next to the sub-vendo VLANs.
   const vlanText = v.vlan.trim();
   if (vlanText === '') {
      payload.vlan = null;
   } else if (!/^\d{1,4}$/.test(vlanText) || Number(vlanText) < 1 || Number(vlanText) > 4094) {
      errors.vlan = 'Type a VLAN number from 1 to 4094, like 22, or leave it empty.';
   } else {
      payload.vlan = Number(vlanText);
   }

   if (v.proto === 'pppoe') {
      if (!v.username.trim())
         errors.username = 'Enter the username your ISP gave you.';
      if (!v.password && !v.hasPassword)
         errors.password = 'Enter the password your ISP gave you.';
      // These cannot survive the router's config file without escaping, and the
      // box refuses them too — catch it here so the operator gets a sentence.
      if (/['\\]/.test(v.username) || /['\\]/.test(v.password))
         errors.password =
            'The username and password cannot contain a quote or a backslash.';
      payload.username = v.username.trim();
      if (v.password) payload.password = v.password;
   }

   if (v.proto === 'static') {
      if (!isIpv4(v.ipaddr))
         errors.ipaddr = 'Enter a valid IP address, like 192.168.1.50.';
      if (!isNetmask(v.netmask))
         errors.netmask = 'Enter a valid subnet mask, like 255.255.255.0.';
      if (!isIpv4(v.gateway)) errors.gateway = 'Enter a valid gateway address.';
      // The quiet failure: the link comes up and nothing routes, because the
      // gateway is on a different network than the address.
      if (
         !errors.ipaddr &&
         !errors.netmask &&
         !errors.gateway &&
         ((toInt(v.ipaddr) ^ toInt(v.gateway)) & toInt(v.netmask)) !== 0
      ) {
         errors.gateway =
            'This gateway is not on the same network as the IP address. Check all three together.';
      }
      const servers = v.dns.split(/[\s,]+/).filter(Boolean);
      if (servers.length > 3) errors.dns = 'Enter at most 3 DNS servers.';
      else if (!servers.every(isIpv4))
         errors.dns = 'Each DNS server must be an IP address.';
      payload.ipaddr = v.ipaddr.trim();
      payload.netmask = v.netmask.trim();
      payload.gateway = v.gateway.trim();
      payload.dns = servers;
   }

   return {
      errors,
      payload: Object.keys(errors).length ? null : payload,
   };
}

/**
 * The router already runs exactly what the form shows, so Apply has nothing to
 * do. An enabled Apply after a success read as "it did not take" (Ray, bench
 * 2026-11-02). Only a confirmed change counts: after a failure Apply stays on.
 */
export function wanFormMatchesRunning(
   form: {
      proto: string;
      vlan: string;
      username: string;
      password: string;
      ipaddr: string;
      netmask: string;
      gateway: string;
      dns: string;
   },
   saved?: {
      status?: string | null;
      proto?: string | null;
      vlanId?: number | null;
      username?: string | null;
      ipaddr?: string | null;
      netmask?: string | null;
      gateway?: string | null;
      dns?: string[] | null;
   } | null
): boolean {
   if (saved?.status !== 'confirmed' || form.proto !== saved.proto) return false;
   const savedVlan = saved.vlanId != null && saved.vlanId > 0 ? String(saved.vlanId) : '';
   if (form.vlan.trim() !== savedVlan) return false;
   if (form.proto === 'pppoe') {
      return form.username.trim() === (saved.username || '') && !form.password;
   }
   if (form.proto === 'static') {
      const dns = form.dns.split(/[\s,]+/).filter(Boolean).join(',');
      return (
         form.ipaddr.trim() === (saved.ipaddr || '') &&
         form.netmask.trim() === (saved.netmask || '') &&
         form.gateway.trim() === (saved.gateway || '') &&
         dns === (saved.dns || []).join(',')
      );
   }
   return true;
}

const WanTab: React.FC<Props> = ({
   deviceId,
   wan,
   routerStatus,
   onRefresh,
   diag,
   lastSeenAt,
   wanTraffic,
}) => {
   const saved = wan ?? null;
   // ⚠️ WHAT THE ROUTER IS RUNNING, not what it was last ASKED to run.
   //
   // This opened on `target` first, and target keeps the last thing requested
   // even after the router rejected it and put the old settings back. So after
   // a failed change the form re-presented the failed settings, filled in and
   // ready to apply again — and said nothing about the fact that they had
   // already been tried and had taken the shop offline for ninety seconds.
   // Ray reopened this modal and found a PPPoE login he had never saved.
   //
   // The one time `target` IS the right answer is while a change is still being
   // judged: the operator asked for it seconds ago and the form should still
   // show it.
   const [proto, setProto] = useState<Proto>(
      (saved?.status === 'applying'
         ? saved?.target || saved?.proto || 'dhcp'
         : saved?.proto || 'dhcp') as Proto
   );
   // Same rule as the mode above: show the tag the router is RUNNING, not the
   // one that was last asked for. After a rollback the router is back on the
   // bare port, so a reverted tag opens empty rather than pre-filled and one
   // click away from taking the shop offline again.
   const [vlan, setVlan] = useState(
      saved?.vlanId != null &&
         saved.vlanId > 0 &&
         (saved.status === 'confirmed' || saved.status === 'applying')
         ? String(saved.vlanId)
         : ''
   );
   const [username, setUsername] = useState(saved?.username || '');
   // Blank means "keep the password already saved". The server only overwrites
   // it when something is actually typed here.
   const [password, setPassword] = useState('');
   const [revealed, setRevealed] = useState(false);
   const [revealing, setRevealing] = useState(false);
   const [ipaddr, setIpaddr] = useState(saved?.ipaddr || '');
   const [netmask, setNetmask] = useState(saved?.netmask || '255.255.255.0');
   const [gateway, setGateway] = useState(saved?.gateway || '');
   const [dns, setDns] = useState((saved?.dns || []).join(', '));
   const [errors, setErrors] = useState<Record<string, string>>({});
   const [confirming, setConfirming] = useState(false);
   const [busy, setBusy] = useState(false);

   const applying = saved?.status === 'applying';
   // U15: tunnel down when Apply was pressed; the server holds it for the next check-in.
   const [queued, setQueued] = useState(false);
   useEffect(() => {
      if (!applying) setQueued(false);
   }, [applying]);
   const hasPassword = !!saved?.hasPassword;

   // While the box is judging a change, keep pulling until it reports back —
   // this is the one screen where the operator is genuinely waiting to find out
   // whether they just cut their own vendo off.
   useEffect(() => {
      if (!applying || !onRefresh) return;
      const t = setInterval(onRefresh, 8000);
      return () => clearInterval(t);
   }, [applying, onRefresh]);

   const alreadyRunning = wanFormMatchesRunning(
      { proto, vlan, username, password, ipaddr, netmask, gateway, dns },
      saved
   );

   const blockedReason =
      routerStatus === 'connected' || routerStatus === undefined
         ? null
         : routerStatus === 'agent-down'
           ? 'The router is reachable but SmartFi is not running on it — restart the router first.'
           : 'The router is offline. Bring it online before changing its internet settings.';

   const validate = (): WanConfigPayload | null => {
      const { errors: next, payload } = validateWanForm({
         proto,
         vlan,
         username,
         password,
         hasPassword,
         ipaddr,
         netmask,
         gateway,
         dns,
      });
      setErrors(next);
      return payload;
   };

   const onApply = () => {
      if (!validate()) return;
      setConfirming(true);
   };

   const doApply = async () => {
      const cfg = validate();
      if (!cfg) {
         setConfirming(false);
         return;
      }
      setBusy(true);
      try {
         const r = await setDeviceWan(deviceId, cfg);
         setConfirming(false);
         setPassword('');
         setRevealed(false);
         // The verdict box says what happens next and stays until it has (a toast
         // faded before Ray could read it, bench 2026-11-02).
         setQueued(!!r.queued);
         onRefresh?.();
      } catch (e: any) {
         toast.error(e?.response?.data?.error || 'Could not change the internet settings.');
      } finally {
         setBusy(false);
      }
   };

   const onReveal = async () => {
      if (revealed) {
         setRevealed(false);
         setPassword('');
         return;
      }
      setRevealing(true);
      try {
         const value = await getDeviceWanPassword(deviceId);
         setPassword(value);
         setRevealed(true);
      } catch {
         toast.error('Could not read the saved password.');
      } finally {
         setRevealing(false);
      }
   };

   const mode = useMemo(() => MODES.find((m) => m.key === proto)!, [proto]);
   const running = saved?.proto
      ? MODES.find((m) => m.key === saved.proto)?.label
      : null;

   return (
      <Wrap>
         <Intro>
            Choose how this router gets its internet. Use <strong>PPPoE login</strong>{' '}
            when the router is wired straight to your ISP&rsquo;s fibre or DSL modem and
            your ISP gave you a username and password.
            {running && (
               <RunningLine>
                  Right now this router is running: <strong>{running}</strong>.
               </RunningLine>
            )}
         </Intro>

         <WanTrafficCard
            deviceId={deviceId}
            diag={diag}
            lastSeenAt={lastSeenAt}
            fallback={wanTraffic}
         />

         {saved?.status && saved.status !== 'idle' && (
            <Verdict $kind={saved.status}>
               {saved.status === 'applying' && (
                  <>
                     {queued ? (
                        <>
                           <b>Waiting for the router's next check-in.</b> The router's link to
                           SmartFi is down right now. It will switch the next time it checks
                           in, usually within a minute, and undo it by itself if it does not
                           work. This page updates when it reports back.
                        </>
                     ) : (
                        <>
                           <b>Testing the new settings.</b> The router is checking whether it
                           can reach the internet. If it cannot, it puts the previous settings
                           back on its own, within about 3 minutes. This page updates when it
                           reports back.
                        </>
                     )}
                  </>
               )}
               {saved.status === 'confirmed' && (
                  <>
                     <b>Working.</b> The router reached the internet on these settings and
                     kept them.
                  </>
               )}
               {saved.status === 'reverted' && (
                  <>
                     <b>Did not work — the router undid it.</b>{' '}
                     {saved.lastError ||
                        'It could not reach the internet, so it put the previous settings back.'}{' '}
                     {saved.vlanId != null && saved.vlanId > 0
                        ? `Check the VLAN tag (${saved.vlanId}) with your ISP or on your switch, then try again.`
                        : saved.target === 'pppoe'
                          ? 'Check the username and password with your ISP, then try again.'
                          : 'Check the settings with your ISP, then try again.'}
                  </>
               )}
               {saved.status === 'failed' && (
                  <>
                     <b>Needs checking on site.</b>{' '}
                     {saved.lastError ||
                        'The router could not reach the internet and could not return to its previous settings.'}
                  </>
               )}
            </Verdict>
         )}

         <Field>
            <Label htmlFor="wan-mode">Mode</Label>
            <Select
               id="wan-mode"
               value={proto}
               disabled={busy || applying}
               onChange={(e) => {
                  setProto(e.target.value as Proto);
                  setErrors({});
                  setConfirming(false);
               }}
            >
               {MODES.map((m) => (
                  <option key={m.key} value={m.key}>
                     {m.label}
                  </option>
               ))}
            </Select>
            <Hint>{mode.blurb}</Hint>
         </Field>

         {proto === 'pppoe' && (
            <>
               <Field>
                  <Label htmlFor="wan-user">ISP username</Label>
                  <Input
                     id="wan-user"
                     value={username}
                     disabled={busy || applying}
                     placeholder="e.g. user@pldtfibr"
                     $invalid={!!errors.username}
                     onChange={(e) => setUsername(e.target.value)}
                  />
                  {errors.username && <Err>{errors.username}</Err>}
               </Field>
               <Field>
                  <Label htmlFor="wan-pass">ISP password</Label>
                  <InputRow>
                     <Input
                        id="wan-pass"
                        type={revealed ? 'text' : 'password'}
                        value={password}
                        disabled={busy || applying}
                        placeholder={hasPassword ? '••••••••••' : 'From your ISP'}
                        $invalid={!!errors.password}
                        onChange={(e) => setPassword(e.target.value)}
                     />
                     {hasPassword && (
                        <GhostBtn
                           type="button"
                           disabled={revealing || busy || applying}
                           onClick={onReveal}
                        >
                           {revealing ? '…' : revealed ? 'Hide' : 'Show'}
                        </GhostBtn>
                     )}
                  </InputRow>
                  {errors.password && <Err>{errors.password}</Err>}
                  {hasPassword && !revealed && (
                     <Hint>Saved. Leave this empty to keep it, or type a new one to change it.</Hint>
                  )}
               </Field>
            </>
         )}

         {proto === 'static' && (
            <>
               <Field>
                  <Label htmlFor="wan-ip">IP address</Label>
                  <Input
                     id="wan-ip"
                     value={ipaddr}
                     disabled={busy || applying}
                     placeholder="192.168.1.50"
                     $invalid={!!errors.ipaddr}
                     onChange={(e) => setIpaddr(e.target.value)}
                  />
                  {errors.ipaddr && <Err>{errors.ipaddr}</Err>}
               </Field>
               <Field>
                  <Label htmlFor="wan-mask">Subnet mask</Label>
                  <Input
                     id="wan-mask"
                     value={netmask}
                     disabled={busy || applying}
                     placeholder="255.255.255.0"
                     $invalid={!!errors.netmask}
                     onChange={(e) => setNetmask(e.target.value)}
                  />
                  {errors.netmask && <Err>{errors.netmask}</Err>}
               </Field>
               <Field>
                  <Label htmlFor="wan-gw">Gateway</Label>
                  <Input
                     id="wan-gw"
                     value={gateway}
                     disabled={busy || applying}
                     placeholder="192.168.1.1"
                     $invalid={!!errors.gateway}
                     onChange={(e) => setGateway(e.target.value)}
                  />
                  {errors.gateway && <Err>{errors.gateway}</Err>}
               </Field>
               <Field>
                  <Label htmlFor="wan-dns">DNS servers (optional)</Label>
                  <Input
                     id="wan-dns"
                     value={dns}
                     disabled={busy || applying}
                     placeholder="1.1.1.1, 8.8.8.8"
                     $invalid={!!errors.dns}
                     onChange={(e) => setDns(e.target.value)}
                  />
                  {errors.dns && <Err>{errors.dns}</Err>}
               </Field>
            </>
         )}

         {/* The VLAN tag sits AFTER the mode's own fields: between the mode and
             the ISP login it read as part of the login (Ray, 2026-10-08). */}
         <Field>
            <Label htmlFor="wan-vlan">Internet VLAN tag (optional)</Label>
            <Input
               id="wan-vlan"
               inputMode="numeric"
               placeholder="leave empty"
               value={vlan}
               disabled={busy || applying}
               onChange={(e) => setVlan(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
               $invalid={!!errors.vlan}
            />
            {errors.vlan && <Err>{errors.vlan}</Err>}
            <Hint>
               Only when the internet reaches this port on a VLAN, for example from a managed
               switch or a fiber modem set to tag it: type that VLAN number, like 22. Otherwise
               leave it empty.
            </Hint>
         </Field>

         {/* Deliberately not a browser popup: the warning is the important part
             and it has to stay on screen next to what it is warning about. */}
         {confirming ? (
            <Confirm>
               <ConfirmTitle>Change how this router gets its internet?</ConfirmTitle>
               <ConfirmBody>
                  Everyone using this vendo drops offline for a moment while the router
                  switches over. If the new settings cannot reach the internet, the router
                  puts the previous ones back by itself after about 3 minutes —
                  so a mistake here costs downtime, not a trip to the shop.
               </ConfirmBody>
               <ConfirmRow>
                  <ApplyBtn type="button" disabled={busy} onClick={doApply}>
                     {busy ? 'Sending…' : 'Yes, change it'}
                  </ApplyBtn>
                  <GhostBtn type="button" disabled={busy} onClick={() => setConfirming(false)}>
                     Cancel
                  </GhostBtn>
               </ConfirmRow>
            </Confirm>
         ) : (
            <Actions>
               <ApplyBtn
                  type="button"
                  disabled={busy || applying || !!blockedReason || alreadyRunning}
                  title={
                     blockedReason ||
                     (alreadyRunning ? 'The router is already running these settings.' : undefined)
                  }
                  onClick={onApply}
               >
                  {applying ? 'Testing…' : 'Apply'}
               </ApplyBtn>
               {blockedReason && <Blocked>{blockedReason}</Blocked>}
            </Actions>
         )}
      </Wrap>
   );
};

export default WanTab;

const Wrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 14px;
`;

const Intro = styled.p`
   margin: 0;
   font-size: 13px;
   line-height: 1.6;
   color: ${colors.GRAY4};
`;

const RunningLine = styled.span`
   display: block;
   margin-top: 4px;
`;

const Verdict = styled.div<{ $kind: string }>`
   padding: 10px 12px;
   border-radius: 10px;
   font-size: 12.5px;
   line-height: 1.6;
   color: ${({ $kind }) =>
      $kind === 'confirmed'
         ? colors.SUCCESS
         : $kind === 'applying'
           ? colors.WARNING
           : colors.DANGER};
   background: ${({ $kind }) =>
      $kind === 'confirmed'
         ? `${colors.SUCCESS}14`
         : $kind === 'applying'
           ? `${colors.WARNING}14`
           : `${colors.DANGER}14`};
   border: 1px solid
      ${({ $kind }) =>
         $kind === 'confirmed'
            ? `${colors.SUCCESS}44`
            : $kind === 'applying'
              ? `${colors.WARNING}44`
              : `${colors.DANGER}44`};
`;

const Field = styled.div`
   display: flex;
   flex-direction: column;
   gap: 5px;
`;

const Label = styled.label`
   font-size: 12.5px;
   font-weight: 700;
`;

const baseInput = `
   width: 100%;
   padding: 9px 11px;
   border-radius: 8px;
   font-size: 13px;
   background: var(--app-surface);
   color: var(--app-text);
`;

const Select = styled.select`
   ${baseInput}
   border: 1px solid ${colors.GRAY11};
`;

const InputRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;

const Input = styled.input<{ $invalid?: boolean }>`
   ${baseInput}
   border: 1px solid ${({ $invalid }) => ($invalid ? colors.DANGER : colors.GRAY11)};
`;

const Hint = styled.div`
   font-size: 11.5px;
   line-height: 1.5;
   color: ${colors.GRAY5};
`;

const Err = styled.div`
   font-size: 11.5px;
   color: ${colors.DANGER};
`;

const Actions = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   flex-wrap: wrap;
`;

const Blocked = styled.div`
   font-size: 11.5px;
   color: ${colors.GRAY5};
   flex: 1;
   min-width: 180px;
`;

const ApplyBtn = styled.button`
   padding: 9px 18px;
   border: none;
   border-radius: 8px;
   background: ${colors.PRIMARY};
   color: #fff;
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;

   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

const GhostBtn = styled.button`
   padding: 9px 14px;
   border: 1px solid ${colors.GRAY11};
   border-radius: 8px;
   background: transparent;
   color: ${colors.GRAY3};
   font-size: 12.5px;
   font-weight: 600;
   cursor: pointer;
   white-space: nowrap;

   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

const Confirm = styled.div`
   padding: 12px;
   border-radius: 10px;
   border: 1px solid ${colors.WARNING}55;
   background: ${colors.WARNING}12;
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

const ConfirmTitle = styled.div`
   font-size: 13px;
   font-weight: 700;
`;

const ConfirmBody = styled.div`
   font-size: 12.5px;
   line-height: 1.6;
   color: ${colors.GRAY4};
`;

const ConfirmRow = styled.div`
   display: flex;
   gap: 10px;
   align-items: center;
`;
